import { useState, useEffect, useCallback } from "react";
import { supabase } from "../../../lib/supabase";
import { useAuth } from "../../../contexts/AuthContext";

interface Project { id: string; title: string; color: string; description?: string | null; }
interface Props { project: Project; workspaceId: string; onUpdate?: () => void; }

interface Task {
  id: string;
  project_id: string;
  title: string;
  description: string | null;
  column_name: "todo" | "inprogress" | "review" | "done";
  assignee_user_id: string | null;
  priority: "low" | "medium" | "high";
  due_date: string | null;
  created_by: string | null;
  created_at: string;
  assignee_name?: string;
}

interface Member { id: string; display_name: string; }

const COLUMNS: { key: Task["column_name"]; label: string; color: string; icon: string }[] = [
  { key: "todo",       label: "할 일",    color: "#555555", icon: "ri-circle-line" },
  { key: "inprogress", label: "진행 중",  color: "#FF9F43", icon: "ri-loader-4-line" },
  { key: "review",     label: "검토 중",  color: "#7A9BFF", icon: "ri-eye-line" },
  { key: "done",       label: "완료",     color: "#00C896", icon: "ri-checkbox-circle-line" },
];

const PRIORITY_CFG = {
  high:   { label: "높음", color: "#E8375F", icon: "ri-arrow-up-line" },
  medium: { label: "중간", color: "#FF9F43", icon: "ri-subtract-line" },
  low:    { label: "낮음", color: "#555555", icon: "ri-arrow-down-line" },
};

const initForm = { title: "", description: "", priority: "medium" as Task["priority"], due_date: "", assignee_user_id: "" };

export default function KanbanBoard({ project, workspaceId, onUpdate }: Props) {
  const { user } = useAuth();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [addForm, setAddForm] = useState<{ column: Task["column_name"] | null; data: typeof initForm }>({ column: null, data: initForm });
  const [editTask, setEditTask] = useState<Task | null>(null);
  const [editForm, setEditForm] = useState({ title: "", description: "", priority: "medium" as Task["priority"], due_date: "", assignee_user_id: "" });
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!workspaceId || !project.id) return;
    setLoading(true);
    const [{ data: taskData }, { data: memberData }] = await Promise.all([
      supabase.from("project_tasks").select("*").eq("project_id", project.id).eq("workspace_id", workspaceId).order("created_at", { ascending: true }),
      supabase.from("workspace_members").select("user_id").eq("workspace_id", workspaceId),
    ]);
    const uids = [...new Set((memberData ?? []).map((m: { user_id: string }) => m.user_id))];
    const { data: profiles } = await supabase.from("profiles").select("id,display_name").in("id", uids);
    const profMap: Record<string, string> = {};
    (profiles ?? []).forEach((p: { id: string; display_name: string }) => { profMap[p.id] = p.display_name; });
    setMembers((profiles ?? []).map((p: { id: string; display_name: string }) => ({ id: p.id, display_name: p.display_name })));
    setTasks((taskData ?? []).map((t: Task) => ({ ...t, assignee_name: t.assignee_user_id ? profMap[t.assignee_user_id] ?? "?" : undefined })));
    setLoading(false);
  }, [project.id, workspaceId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    if (!project.id) return;
    const channel = supabase.channel(`kanban-${project.id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "project_tasks", filter: `project_id=eq.${project.id}` }, () => { fetchAll(); })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [project.id, fetchAll]);

  const handleAddTask = async () => {
    if (!user || !addForm.column || !addForm.data.title.trim()) return;
    setSaving(true);
    await supabase.from("project_tasks").insert({
      project_id: project.id,
      workspace_id: workspaceId,
      title: addForm.data.title.trim(),
      description: addForm.data.description.trim() || null,
      column_name: addForm.column,
      priority: addForm.data.priority,
      due_date: addForm.data.due_date || null,
      assignee_user_id: addForm.data.assignee_user_id || null,
      created_by: user.id,
    });
    setSaving(false);
    setAddForm({ column: null, data: initForm });
    onUpdate?.();
  };

  const handleMoveTask = async (taskId: string, newCol: Task["column_name"]) => {
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, column_name: newCol } : t));
    await supabase.from("project_tasks").update({ column_name: newCol, updated_at: new Date().toISOString() }).eq("id", taskId);
  };

  const openEdit = (task: Task) => {
    setEditTask(task);
    setEditForm({ title: task.title, description: task.description ?? "", priority: task.priority, due_date: task.due_date ?? "", assignee_user_id: task.assignee_user_id ?? "" });
  };

  const handleSaveEdit = async () => {
    if (!editTask || !editForm.title.trim()) return;
    setSaving(true);
    const mname = editForm.assignee_user_id ? members.find(m => m.id === editForm.assignee_user_id)?.display_name : undefined;
    await supabase.from("project_tasks").update({
      title: editForm.title.trim(),
      description: editForm.description.trim() || null,
      priority: editForm.priority,
      due_date: editForm.due_date || null,
      assignee_user_id: editForm.assignee_user_id || null,
      updated_at: new Date().toISOString(),
    }).eq("id", editTask.id);
    setTasks(prev => prev.map(t => t.id === editTask.id ? { ...t, ...editForm, assignee_name: mname, description: editForm.description.trim() || null, due_date: editForm.due_date || null, assignee_user_id: editForm.assignee_user_id || null } : t));
    setSaving(false);
    setEditTask(null);
  };

  const handleDeleteTask = async (taskId: string) => {
    setTasks(prev => prev.filter(t => t.id !== taskId));
    await supabase.from("project_tasks").delete().eq("id", taskId);
    setEditTask(null);
    onUpdate?.();
  };

  const getColTasks = (col: Task["column_name"]) => tasks.filter(t => t.column_name === col);
  const doneCount = tasks.filter(t => t.column_name === "done").length;
  const pct = tasks.length > 0 ? Math.round((doneCount / tasks.length) * 100) : 0;

  return (
    <>
      <div className="flex flex-col h-full">
        {/* 상단 헤더 */}
        <div className="px-5 py-3 border-b border-white/[0.05] flex items-center gap-3 shrink-0">
          <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: project.color }}></div>
          <div className="flex-1 min-w-0">
            <div className="text-[#F2F2F2] text-sm font-semibold truncate">{project.title}</div>
            {project.description && <div className="text-[#3D3D3D] text-[10px] truncate">{project.description}</div>}
          </div>
          {/* 진행도 */}
          <div className="flex items-center gap-3 shrink-0">
            <div className="text-[10px] text-[#555555]">{doneCount}/{tasks.length} 완료</div>
            <div className="w-20 h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: project.color }} />
            </div>
            <div className="text-[10px] font-bold" style={{ color: project.color }}>{pct}%</div>
          </div>
        </div>

        {/* 칸반 컬럼 */}
        <div className="flex-1 overflow-x-auto overflow-y-hidden">
          <div className="flex h-full gap-3 p-4" style={{ minWidth: 800 }}>
            {COLUMNS.map(col => {
              const colTasks = getColTasks(col.key);
              const isAdding = addForm.column === col.key;
              return (
                <div key={col.key} className="flex flex-col flex-1 min-w-0 bg-white/[0.02] rounded-xl border border-white/[0.04]" style={{ minWidth: 220 }}>
                  {/* 컬럼 헤더 */}
                  <div className="px-3 py-2.5 flex items-center gap-2 border-b border-white/[0.04] shrink-0">
                    <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: col.color }}></div>
                    <i className={`${col.icon} text-xs`} style={{ color: col.color }}></i>
                    <span className="text-xs font-semibold" style={{ color: col.color }}>{col.label}</span>
                    <span className="ml-auto text-[10px] text-[#444444] bg-white/[0.04] px-1.5 py-0.5 rounded-full">{colTasks.length}</span>
                  </div>

                  {/* 태스크 목록 */}
                  <div className="flex-1 overflow-y-auto px-2 py-2 space-y-1.5">
                    {loading ? [...Array(2)].map((_, i) => (
                      <div key={i} className="h-16 bg-white/[0.02] rounded-lg animate-pulse border border-white/[0.04]" />
                    )) : colTasks.map(task => (
                      <div key={task.id}
                        className="bg-[#1A1C25] border border-white/[0.07] rounded-xl px-3 py-2.5 cursor-pointer hover:border-white/[0.15] transition-all group"
                        onClick={() => openEdit(task)}>
                        <div className="flex items-start gap-1.5 mb-1.5">
                          <i className={`${PRIORITY_CFG[task.priority].icon} text-xs shrink-0 mt-0.5`} style={{ color: PRIORITY_CFG[task.priority].color }}></i>
                          <span className="text-[#CCCCCC] text-xs leading-relaxed line-clamp-2">{task.title}</span>
                        </div>
                        {task.description && (
                          <p className="text-[#444444] text-[9px] leading-relaxed line-clamp-1 mb-1.5">{task.description}</p>
                        )}
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {task.assignee_name && (
                            <div className="flex items-center gap-1 bg-white/[0.05] rounded-full px-1.5 py-0.5">
                              <div className="w-3 h-3 rounded-full flex items-center justify-center text-[7px] font-bold" style={{ backgroundColor: project.color + "40", color: project.color }}>
                                {task.assignee_name[0]}
                              </div>
                              <span className="text-[9px] text-[#777777]">{task.assignee_name}</span>
                            </div>
                          )}
                          {task.due_date && (
                            <div className={`flex items-center gap-0.5 text-[9px] ${new Date(task.due_date) < new Date() && task.column_name !== "done" ? "text-[#E8375F]" : "text-[#444444]"}`}>
                              <i className="ri-calendar-line text-[9px]"></i>{task.due_date}
                            </div>
                          )}
                        </div>
                        {/* 이동 버튼 */}
                        <div className="hidden group-hover:flex items-center gap-1 mt-2 pt-2 border-t border-white/[0.05]" onClick={e => e.stopPropagation()}>
                          {COLUMNS.filter(c => c.key !== col.key).map(c => (
                            <button key={c.key} type="button"
                              onClick={() => handleMoveTask(task.id, c.key)}
                              className="flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded border transition-all cursor-pointer"
                              style={{ color: c.color, borderColor: `${c.color}30`, backgroundColor: `${c.color}10` }}>
                              → {c.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}

                    {/* 추가 폼 */}
                    {isAdding && (
                      <div className="bg-[#1A1C25] border border-[#4B6CF7]/30 rounded-xl px-3 py-2.5 space-y-2">
                        <input type="text" value={addForm.data.title} onChange={e => setAddForm(f => ({ ...f, data: { ...f.data, title: e.target.value } }))}
                          placeholder="태스크 제목..." autoFocus maxLength={100}
                          className="w-full bg-transparent text-[#CCCCCC] text-xs placeholder-[#3A3A3A] focus:outline-none" />
                        <textarea value={addForm.data.description} onChange={e => setAddForm(f => ({ ...f, data: { ...f.data, description: e.target.value } }))}
                          placeholder="설명 (선택)" rows={2} maxLength={300}
                          className="w-full bg-transparent text-[#888888] text-[10px] placeholder-[#2A2A2A] focus:outline-none resize-none" />
                        <div className="flex gap-1.5">
                          <select value={addForm.data.priority} onChange={e => setAddForm(f => ({ ...f, data: { ...f.data, priority: e.target.value as Task["priority"] } }))}
                            className="flex-1 bg-[#0C0C0E] text-[#888888] text-[10px] rounded px-1.5 py-1 border border-white/[0.06] focus:outline-none cursor-pointer">
                            <option value="high">높음</option><option value="medium">중간</option><option value="low">낮음</option>
                          </select>
                          <select value={addForm.data.assignee_user_id} onChange={e => setAddForm(f => ({ ...f, data: { ...f.data, assignee_user_id: e.target.value } }))}
                            className="flex-1 bg-[#0C0C0E] text-[#888888] text-[10px] rounded px-1.5 py-1 border border-white/[0.06] focus:outline-none cursor-pointer">
                            <option value="">담당자</option>
                            {members.map(m => <option key={m.id} value={m.id}>{m.display_name}</option>)}
                          </select>
                        </div>
                        <input type="date" value={addForm.data.due_date} onChange={e => setAddForm(f => ({ ...f, data: { ...f.data, due_date: e.target.value } }))}
                          className="w-full bg-[#0C0C0E] text-[#888888] text-[10px] rounded px-1.5 py-1 border border-white/[0.06] focus:outline-none" />
                        <div className="flex gap-1.5">
                          <button type="button" onClick={() => setAddForm({ column: null, data: initForm })}
                            className="flex-1 py-1.5 text-[10px] text-[#555555] border border-white/[0.06] rounded cursor-pointer hover:text-[#888888] transition-colors whitespace-nowrap">취소</button>
                          <button type="button" onClick={handleAddTask} disabled={!addForm.data.title.trim() || saving}
                            className="flex-1 py-1.5 text-[10px] text-white font-semibold rounded cursor-pointer transition-colors whitespace-nowrap disabled:opacity-50"
                            style={{ backgroundColor: project.color }}>
                            {saving ? "추가 중..." : "추가"}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* 추가 버튼 */}
                  {!isAdding && (
                    <div className="px-2 pb-2 shrink-0">
                      <button type="button" onClick={() => setAddForm({ column: col.key, data: initForm })}
                        className="w-full flex items-center gap-1.5 px-3 py-2 text-[#333333] hover:text-[#666666] hover:bg-white/[0.03] rounded-lg transition-all cursor-pointer text-xs">
                        <i className="ri-add-line text-sm"></i>태스크 추가
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* 태스크 편집 모달 */}
      {editTask && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6" onClick={() => setEditTask(null)}>
          <div className="bg-[#1C1F2E] rounded-2xl border border-white/[0.08] p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-[#F2F2F2] font-bold text-base">태스크 편집</h3>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => handleDeleteTask(editTask.id)}
                  className="w-7 h-7 flex items-center justify-center text-[#3D3D3D] hover:text-[#E8375F] cursor-pointer transition-colors" title="삭제">
                  <i className="ri-delete-bin-line text-sm"></i>
                </button>
                <button type="button" onClick={() => setEditTask(null)}
                  className="w-7 h-7 flex items-center justify-center text-[#3D3D3D] hover:text-[#777777] cursor-pointer transition-colors">
                  <i className="ri-close-line text-base"></i>
                </button>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[#666666] text-xs mb-1 block">제목</label>
                <input type="text" value={editForm.title} onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} maxLength={100}
                  className="w-full bg-[#0C0C0E] text-[#CCCCCC] text-sm px-3 py-2.5 rounded-lg border border-white/[0.08] focus:outline-none focus:border-[#4B6CF7]/40" />
              </div>
              <div>
                <label className="text-[#666666] text-xs mb-1 block">설명</label>
                <textarea value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} rows={3} maxLength={500}
                  className="w-full bg-[#0C0C0E] text-[#CCCCCC] text-sm px-3 py-2.5 rounded-lg border border-white/[0.08] focus:outline-none focus:border-[#4B6CF7]/40 resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[#666666] text-xs mb-1 block">우선순위</label>
                  <select value={editForm.priority} onChange={e => setEditForm(f => ({ ...f, priority: e.target.value as Task["priority"] }))}
                    className="w-full bg-[#0C0C0E] text-[#CCCCCC] text-sm px-3 py-2 rounded-lg border border-white/[0.08] focus:outline-none cursor-pointer">
                    <option value="high">🔴 높음</option><option value="medium">🟡 중간</option><option value="low">⚪ 낮음</option>
                  </select>
                </div>
                <div>
                  <label className="text-[#666666] text-xs mb-1 block">담당자</label>
                  <select value={editForm.assignee_user_id} onChange={e => setEditForm(f => ({ ...f, assignee_user_id: e.target.value }))}
                    className="w-full bg-[#0C0C0E] text-[#CCCCCC] text-sm px-3 py-2 rounded-lg border border-white/[0.08] focus:outline-none cursor-pointer">
                    <option value="">없음</option>
                    {members.map(m => <option key={m.id} value={m.id}>{m.display_name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-[#666666] text-xs mb-1 block">마감일</label>
                <input type="date" value={editForm.due_date} onChange={e => setEditForm(f => ({ ...f, due_date: e.target.value }))}
                  className="w-full bg-[#0C0C0E] text-[#CCCCCC] text-sm px-3 py-2 rounded-lg border border-white/[0.08] focus:outline-none" />
              </div>
              {/* 컬럼 이동 */}
              <div>
                <label className="text-[#666666] text-xs mb-2 block">컬럼 이동</label>
                <div className="flex gap-1.5 flex-wrap">
                  {COLUMNS.map(c => (
                    <button key={c.key} type="button"
                      onClick={() => { handleMoveTask(editTask.id, c.key); setEditTask(prev => prev ? { ...prev, column_name: c.key } : null); }}
                      className="flex-1 py-1.5 rounded-lg border text-[11px] font-medium cursor-pointer transition-all whitespace-nowrap"
                      style={editTask.column_name === c.key
                        ? { borderColor: `${c.color}50`, backgroundColor: `${c.color}18`, color: c.color }
                        : { borderColor: "rgba(255,255,255,0.06)", color: "#555555" }}>
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setEditTask(null)}
                  className="flex-1 py-2.5 rounded-xl border border-white/[0.08] text-[#666666] text-sm cursor-pointer hover:text-[#888888] transition-colors whitespace-nowrap">취소</button>
                <button type="button" onClick={handleSaveEdit} disabled={!editForm.title.trim() || saving}
                  className="flex-1 py-2.5 rounded-xl text-white text-sm font-semibold cursor-pointer transition-colors whitespace-nowrap disabled:opacity-50"
                  style={{ backgroundColor: project.color }}>
                  {saving ? "저장 중..." : "저장"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
